import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OrderForm, type OrderPayment } from "@/components/OrderForm";

export function OrderEdit() {
  const { id } = useParams<{ id: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [payment, setPayment] = useState<OrderPayment | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchPayment() {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await fetch(`/api/payments/${id}`);
        if (!response.ok) {
          setPayment(null);
          return;
        }
        const data = await response.json();
        const p = data.payment as OrderPayment | undefined;
        setPayment(p ?? null);
      } catch (e) {
        console.error(e);
        setPayment(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchPayment();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    setBreadcrumbs([
      { label: "Sales", path: "/products" },
      { label: "Orders", path: "/sales/orders" },
      {
        label: payment?.customerEmail || "Edit order",
        path: `/sales/orders/${id}`,
      },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, [id, payment?.customerEmail, setBreadcrumbs, clearBreadcrumbs]);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="flex justify-center items-center min-h-[40vh]">
          <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="py-4 space-y-4">
          <p className="text-muted-foreground">Order not found.</p>
          <Button variant="outline" asChild>
            <Link to="/sales/orders">Back to orders</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
      <OrderForm
        mode="edit"
        payment={payment}
        patchPayment={(patch) =>
          setPayment((prev) => {
            if (!prev) return null;
            const next = { ...prev, ...patch };
            if ("lead" in patch && patch.lead === undefined) {
              delete next.lead;
            }
            return next;
          })
        }
        onSaved={() => {}}
      />
    </div>
  );
}
